import { useState } from "react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { BACKEND_URL } from "@/constants/api";
import { LEGAL_VERSIONS } from "@/constants/legal";
import { Button } from "@/components/ui/button";

export function TosConsentModal({ onAccepted }) {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/user/accept-tos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tosVersion: LEGAL_VERSIONS.tos }),
      });
      if (!res.ok) throw new Error("Request failed");
      onAccepted();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 pt-6 pb-5 border-b border-border flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} className="text-primary" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground leading-snug m-0">Welcome to EasyReports</h2>
            <p className="text-xs text-muted-foreground m-0 mt-0.5">Before you continue, please review and accept our terms.</p>
          </div>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-muted-foreground m-0">
            EasyReports stores student report card data that you enter. You are responsible for
            ensuring you have the authority to input and store this information, and that you
            handle it in accordance with your school board&rsquo;s privacy policies.
          </p>

          <label className="flex items-start gap-3 cursor-pointer select-none rounded-xl border border-border bg-muted/30 px-4 py-3.5">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="w-4 h-4 mt-0.5 rounded accent-primary shrink-0"
            />
            <span className="text-sm text-foreground leading-snug">
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                Privacy Policy
              </a>
            </span>
          </label>

          {error && (
            <p className="text-sm text-destructive m-0">{error}</p>
          )}
        </div>

        <div className="px-6 pb-6 flex gap-2">
          <Button
            onClick={handleAccept}
            disabled={!checked || loading}
            className="flex-1"
          >
            {loading ? "Saving..." : "Accept & Continue"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSignOut}
            disabled={loading}
          >
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}
